
import { FormValues } from "@/types/loan";
import { SORTTER_REDIRECT_URL, SORTTER_OFFER_ID, SORTTER_AFFILIATE_ID } from "./constants";
import { toast } from "sonner";
import { LoanPurpose } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeString, sanitizeEmail, validateEmail } from "@/utils/security";

// Function to submit loan application to Sortter API via Supabase Edge Function
export const submitToSortter = async (formData: FormValues): Promise<string> => {
  try {
    // Validate email first
    if (!formData.email || !validateEmail(formData.email)) {
      console.error("Invalid email for Sortter API submission");
      return `application-${Date.now()}`;
    }
    
    // Sanitize input data
    const sanitizedEmail = sanitizeEmail(formData.email);
    const sanitizedFirstName = sanitizeString(formData.firstName || '');
    const sanitizedLastName = sanitizeString(formData.lastName || '');
    const sanitizedPhone = sanitizeString(formData.phoneNumber || '');
    
    // Map loan purpose to Sortter format
    const mappedLoanPurpose = mapLoanPurpose(formData.loanPurpose);
    
    // Prepare data for Sortter API
    const sortterData = {
      email: sanitizedEmail,
      firstName: sanitizedFirstName,
      lastName: sanitizedLastName,
      phoneNumber: sanitizedPhone,
      loanAmount: Number(formData.loanAmount) || 0,
      loanTerm: Number(formData.loanTerm) || 0,
      loanPurpose: mappedLoanPurpose,
      education: formData.education ? sanitizeString(formData.education) : '',
      natureOfWork: formData.natureOfWork ? sanitizeString(formData.natureOfWork) : '',
      startOfEmployment: formData.startOfEmployment ? sanitizeString(formData.startOfEmployment) : '',
      hasCoApplicant: Boolean(formData.hasCoApplicant),
    };

    console.log("Submitting to Sortter API via Edge Function:", sortterData);

    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('sortter-api', {
      body: sortterData
    });

    // Handle error from edge function
    if (error) {
      console.error("Edge Function error:", error);
      return `application-${Date.now()}`;
    }

    console.log("Edge Function response:", data);
    
    // Return token from response with validation
    if (data && data.token && typeof data.token === 'string') {
      const sanitizedToken = sanitizeString(data.token);
      console.log("Received token from Sortter API:", sanitizedToken);
      return sanitizedToken;
    } else {
      console.error("Missing or invalid token in response:", data);
      return `application-${Date.now()}`;
    }
  } catch (error) {
    console.error("Error submitting to Sortter:", error);
    // Always return fallback token, never return null
    return `application-${Date.now()}`;
  }
};

// Map our loan purpose values to Sortter's expected format
const mapLoanPurpose = (purpose?: string): string => {
  if (!purpose) return 'other';
  
  const purposeMap: Record<string, string> = {
    [LoanPurpose.Renovation]: 'renovation',
    [LoanPurpose.FurnitureOrElectronics]: 'furniture_electronics',
    [LoanPurpose.CarOrBoatOrMotorcycle]: 'vehicle',
    [LoanPurpose.LivingCosts]: 'living_costs',
    [LoanPurpose.Healthcare]: 'healthcare',
    [LoanPurpose.Study]: 'education',
    [LoanPurpose.HobbiesOrPet]: 'hobbies_pets',
    [LoanPurpose.Holiday]: 'travel',
    [LoanPurpose.Wedding]: 'wedding',
    [LoanPurpose.RepayExistingLoans]: 'debt_consolidation',
    [LoanPurpose.Taxes]: 'taxes',
    [LoanPurpose.Investments]: 'investments',
    [LoanPurpose.LandOrProperty]: 'real_estate',
    [LoanPurpose.Company]: 'business',
    [LoanPurpose.Other]: 'other'
  };
  
  // Sanitize the mapped purpose
  const mappedPurpose = purposeMap[purpose] || 'other';
  return sanitizeString(mappedPurpose);
};

// Get redirect URL with enhanced security
export const getSortterRedirectUrl = (_token?: string): string => {
  const sanitizedOfferId = sanitizeString(SORTTER_OFFER_ID);
  const sanitizedAffId = sanitizeString(SORTTER_AFFILIATE_ID);
  return `https://sortter.scaletrk.com/click?o=${sanitizedOfferId}&a=${sanitizedAffId}`;
};

// Validate loan application data
export const validateSortterApplication = (data: FormValues): boolean => {
  // Check required fields
  if (!data.firstName || !data.lastName || !data.email || !data.phoneNumber) {
    return false;
  }
  
  // Check loan amount and term
  if (!data.loanAmount || data.loanAmount < 1000 || !data.loanTerm || data.loanTerm < 1) {
    return false;
  }
  
  // Check terms acceptance
  if (!data.termsAccepted) {
    return false;
  }
  
  return true;
};
